import { FloatingFormItem } from '@/components/_common';
import { QuotationSuppliersOffers } from '@/core/domain/stock/QuotationsOffers/QuotationsOffers.types';
import { Form, Input, Modal, ModalProps, Upload } from 'antd';
import Table, { ColumnsType } from 'antd/lib/table';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import { UploadCloud } from 'react-feather';
import { RcFile } from 'antd/lib/upload';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Attachments } from '@/core/domain/stock/Quotations/Quotations.types';

type Props = Omit<ModalProps, 'onCancel'> & {
  onCancel: () => void;
  suppliers: QuotationSuppliersOffers[];
  onCallback: (values: SendRequestPurchaseModalFormValues) => void;
  setFileList: React.Dispatch<React.SetStateAction<RcFile[]>>;
  loading: boolean;
};

export interface SendRequestPurchaseModalFormValues {
  suppliers: QuotationSuppliersOffers[];
  subject: string;
  content: string;
  attachments: Attachments[];
}

const SendRequestPurchaseModal: React.FC<Props> = ({
  open,
  onCancel,
  suppliers,
  onCallback,
  setFileList,
  loading,
}) => {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const form = Form.useFormInstance<SendRequestPurchaseModalFormValues>();
  const intl = useIntl();

  useEffect(() => {
    if (suppliers.length) {
      form.setFieldsValue({
        suppliers: suppliers.reduce((acc, item) => {
          const hasSupplier = acc.some(
            (a) => a.supplier.id === item.supplier.id,
          );

          if (!hasSupplier) {
            return [...acc, item];
          }

          return acc;
        }, [] as QuotationSuppliersOffers[]),
      });
    }
  }, [suppliers]);

  const handleCancel = () => {
    onCancel();
  };

  const handleSend = async () => {
    onCallback(form.getFieldsValue());
  };

  const columns: ColumnsType<QuotationSuppliersOffers> = [
    {
      key: 'supplierName',
      dataIndex: ['supplier', 'name'],
      title: 'Fornecedor',
      width: 300,
    },
    {
      key: 'supplierEmail',
      title: 'Email',
      render: (_, __, index) => (
        <>
          <Form.Item
            style={{ margin: 0 }}
            name={[index, 'supplier', 'email']}
            rules={[
              {
                type: 'email',
                message: intl.formatMessage({
                  id: 'commons.form.errors.invalidEmail',
                }),
              },
              defaultEmptyFieldError,
            ]}
          >
            <Input type="email" />
          </Form.Item>
        </>
      ),
      width: 300,
    },
  ];

  return (
    <Modal
      title="Enviar email compra"
      open={open}
      onCancel={handleCancel}
      onOk={handleSend}
      okText="Enviar"
      confirmLoading={loading}
      width={1200}
    >
      <Form.List name="suppliers">
        {() => (
          <Table
            loading={!suppliers.length}
            rowKey={(row) => row.id}
            dataSource={form.getFieldValue('suppliers')}
            columns={columns}
            pagination={false}
          />
        )}
      </Form.List>

      <div style={{ margin: '32px 0' }}>
        <FloatingFormItem
          name="subject"
          label={intl.formatMessage({
            id: 'quotations.details.sendQuotationRequest.subject',
          })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'commons.form.errors.requiredField',
              }),
            },
          ]}
        >
          <Input />
        </FloatingFormItem>
        <FloatingFormItem
          name="content"
          label={intl.formatMessage({
            id: 'quotations.details.sendQuotationRequest.content',
          })}
        >
          <Input.TextArea rows={5} />
        </FloatingFormItem>

        <Form.Item
          noStyle
          name="attachments"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e && e.fileList;
          }}
        >
          <Upload.Dragger
            beforeUpload={(file) => {
              setFileList((prev) => [...prev, file]);
              return false;
            }}
            onRemove={({ uid }) => {
              setFileList((prev) => {
                if (prev.length) {
                  return prev.filter((file) => file.uid !== uid);
                }
                return [];
              });
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <AntdFeatherIcon icon={<UploadCloud />} size={32} />
              <p
                style={{
                  marginTop: '12px',
                  fontSize: '1.05em',
                  fontWeight: '500',
                }}
              >
                <FormattedMessage id="quotations.details.sendQuotationRequest.attachmentText" />
              </p>
              <p style={{ color: 'rgb(0, 0, 0, 0.5)', marginTop: '4px' }}>
                <FormattedMessage id="quotations.details.sendQuotationRequest.attachmentHint" />
              </p>
            </div>
          </Upload.Dragger>
        </Form.Item>
      </div>
    </Modal>
  );
};

export default SendRequestPurchaseModal;
